import DOMPurify from "dompurify";

/**
 * Sanitizes a string of untrusted HTML — produced by the Tiptap editor on the
 * freelancer profile — before we hand it to React's <c>dangerouslySetInnerHTML</c>.
 *
 * We delegate to DOMPurify (industry-standard, audited, ~15 kB) and lock it down
 * to a strict whitelist that covers exactly what Tiptap can emit for the
 * presentation / technical-stack fields. Anything else — scripts, iframes,
 * inline event handlers, style attributes, links, images, data: URIs — is
 * stripped silently.
 *
 * The function is safe to call in SSR / test contexts: when `window` is
 * undefined we fall back to a tag-stripping regex so the caller still gets a
 * plain-text approximation instead of a crash.
 */
const ALLOWED_TAGS = [
	"p",
	"br",
	"strong",
	"b",
	"em",
	"i",
	"u",
	"ul",
	"ol",
	"li",
	"span"
];

// DOMPurify config kept module-level so the cost is paid once.
// Note: we deliberately DO NOT set USE_PROFILES — it expands the whitelist to
// every HTML tag DOMPurify considers safe by default (including <a>, <img>,
// <div>, <h*>) which would defeat our stricter "inline formatting only" policy.
const PURIFY_CONFIG: DOMPurify.Config = {
	ALLOWED_TAGS,
	ALLOWED_ATTR: [], // No attributes allowed — kills href / style / on* / data-* / class entirely.
	KEEP_CONTENT: true, // For disallowed tags, keep the inner text so legitimate copy isn't dropped.
	ALLOW_DATA_ATTR: false,
	ALLOW_UNKNOWN_PROTOCOLS: false,
	RETURN_TRUSTED_TYPE: false
};

export function sanitizeHtml(input: string | null | undefined): string {
	if (!input) {
		return "";
	}

	// In a non-browser context (SSR / Jest without jsdom), DOMPurify cannot
	// run — strip every tag so callers still get something legible.
	if (typeof window === "undefined") {
		return input.replace(/<[^>]*>/g, "");
	}

	return DOMPurify.sanitize(input, PURIFY_CONFIG) as string;
}
